import React, {useEffect, useRef, useState} from "react";
import {Button, Checkbox, Flex, Form, Input, Select, message} from "antd";
import {LoginOutlined, RightOutlined, StepForwardOutlined} from "@ant-design/icons";
import i18next from "i18next";
import "react-multi-date-picker/styles/layouts/mobile.css";
import * as Setting from "../Setting";
import {goToLink} from "../Setting";
import {CountryCodeSelect} from "../common/select/CountryCodeSelect";
import {CURRENT_EXECUTING_STEP, RsoSendCodeInput, SEND_CODE_METHOD} from "../common/RsoSendCodeInput";
import {AgreementModal} from "../common/modal/AgreementModal";
import {useCaptcha} from "./hooks/useCaptcha";
import {useOtp} from "./hooks/useOtp";
import {usePasswordValidation} from "./hooks/usePasswordValidation";
import {useGoBack} from "./hooks/useGoBack";
import {buildRules} from "./utils/formRules";
import {useEmployee} from "../providers/EmployeeContext";
import {getAuthUrl} from "./Provider";
import WheelPicker from "../common/WheelPicker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import RsoSendCodeOtp from "../common/RsoSendCodeOtp";
import FaceCapturing from "./FaceCapturing";

const DynamicForm = (props) => {
  const {
    application,
    isForeigner,
    formConfig,
    shouldReloadCaptcha,
    mfaType,
    stepProviders,
    actions,
    stateId,
    onFormConfigChange,
  } = props;

  console.log("actions", actions && actions.length > 0);
  const {isEmployee, setIsEmployee} = useEmployee();
  const formRef = useRef(null);
  const [dateStates, setDateStates] = useState({});
  const [currentFormConfig, setCurrentFormConfig] = useState({});
  useEffect(() => {
    if (formConfig) {
      setCurrentFormConfig({
        ...formConfig,
        initialValues: {
          ...formConfig.initialValues,
          is_employee: isEmployee,
        },
      });
    }
  }, [formConfig]);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSent, setMfaSent] = useState(false);

  const [sendCodeMethod, setSendCodeMethod] = useState(SEND_CODE_METHOD.SMART_LOGIN);
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const formValues = Form.useWatch([], form);
  const [honaPromptText, setHonaPromptText] = useState("");
  const countryCodeValue = Form.useWatch("country_code", form);
  const phoneValue = Form.useWatch("phone", form);
  const initialCountryCode = formConfig.initialValues?.country_code;
  const initialPhone = formConfig.initialValues?.phone;

  const isFastLogin =
    currentFormConfig.type === CURRENT_EXECUTING_STEP.FAST_LOGIN ||
    currentFormConfig.stepName === CURRENT_EXECUTING_STEP.FAST_LOGIN;

  const country_code = isFastLogin ? countryCodeValue : initialCountryCode;

  const phone = isFastLogin ? phoneValue : initialPhone;

  useEffect(() => {
    form
      .validateFields({validateOnly: true})
      .then(() => {
        setIsFormValid(true);
      })
      .catch(() => {
        setIsFormValid(false);
      });
  }, [formValues, form]);

  useEffect(() => {
    if (isFastLogin) {
      setSendCodeMethod(SEND_CODE_METHOD.FAST_LOGIN);
    }
  }, [isFastLogin]);

  useEffect(() => {
    if (formConfig.initialValues?.is_employee !== undefined) {
      setIsEmployee(formConfig.initialValues.is_employee);
    }
  }, [formConfig.initialValues?.is_employee, setIsEmployee]);

  useEffect(() => {
    if ((formConfig?.type || formConfig?.stepName) !== "hona_aliveness_detection") {
      return;
    }
    const iv = formConfig.initialValues || {};
    const text = iv.text || iv.honaExpectedText || "";
    const nextValues = {
      uniqueKey: iv.uniqueKey || iv.honaUniqueKey || "",
      text,
      nationalId: iv.nationalId || iv.national_id || "",
    };
    if (iv.personalPictureDataUri === "" || iv.testVideoDataUri === "") {
      nextValues.personalPictureDataUri = "";
      nextValues.testVideoDataUri = "";
    }
    setHonaPromptText(text);
    form.setFieldsValue(nextValues);
  }, [formConfig.type, formConfig.initialValues, form]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.setFieldsValue({is_employee: isEmployee});
    }
  }, [isEmployee]);

  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const captcha = useCaptcha({
    application,
    responseCurrentStep: currentFormConfig.type || currentFormConfig.stepName,
    shouldReloadCaptcha,
  });

  const otp = useOtp();
  const passwordApi = usePasswordValidation();
  // handle in backend
  // const employeeFlow = useEmployeeFlow({
  //   formRef,
  //   application,
  //   serviceUrl: props.serviceUrl,
  //   returnUrl: props.returnUrl,
  //   location: props.location,
  //   onFormConfigChange,
  // });

  const goBackApi = useGoBack({
    formRef,
    application,
    serviceUrl: props.serviceUrl,
    returnUrl: props.returnUrl,
    location: props.location,
    onFormConfigChange,
  });

  useEffect(() => {
    if (props.onStepStateChange) {
      const isFirst = !goBackApi.canGoBack(formConfig);
      props.onStepStateChange(isFirst);
    }
  }, [formConfig]);

  const handleSubmit = (values) => {
    if (!captcha.isCaptchaValid) {
      return;
    }
    if (!otp.isOtpValid) {
      Setting.showMessage("error", i18next.t("general:Please enter a valid OTP code"));
      return;
    }

    const formValuesAll = form.getFieldsValue(true);
    const finalValues = {
      ...formValuesAll,
      ...values,
      is_returning: false,
      is_employee: isEmployee,
      ...(captcha.captchaToken && captcha.clientSecret
        ? {
          captchaToken: captcha.captchaToken,
          captchaSecret: captcha.clientSecret,
          captchaType: "Default",
        }
        : {}),
    };

    if ((formConfig?.type || formConfig?.stepName) === "hona_aliveness_detection") {
      finalValues.personalPictureDataUri =
        finalValues.personalPictureDataUri || formValuesAll.personalPictureDataUri;
      finalValues.testVideoDataUri =
        finalValues.testVideoDataUri || formValuesAll.testVideoDataUri;
      finalValues.uniqueKey = finalValues.uniqueKey || formValuesAll.uniqueKey;
      finalValues.text = finalValues.text || formValuesAll.text;
      finalValues.nationalId = finalValues.nationalId || formValuesAll.nationalId;
    }

    formConfig.onSubmit(finalValues);
  };

  const handleProviderClick = (provider) => {
    const selectedProvider = stepProviders.find((item) => item.name === provider.metadata.default);
    localStorage.setItem("rso_smart_login_state_id", stateId);
    localStorage.setItem("rso_selected_provider", selectedProvider.name);
    localStorage.setItem("login_path", window.location.pathname);
    localStorage.setItem("rso_smart_login", "true");
    goToLink(getAuthUrl(application, selectedProvider, "signup"));
  };

  const rawDataRequired = formConfig?.dataRequired || [];
  const fields = {};

  if (Array.isArray(rawDataRequired)) {
    rawDataRequired.forEach((f) => {
      if (f && f.name) {
        fields[f.name] = f;
      }
    });
  } else if (typeof rawDataRequired === "object" && rawDataRequired !== null) {
    Object.keys(rawDataRequired).forEach((key) => {
      fields[key] = rawDataRequired[key];
      if (!fields[key].name) {
        fields[key].name = key;
      }
    });
  }

  const detectedType = formConfig.detectedType;
  const hasOauthProvider = Array.isArray(rawDataRequired) && rawDataRequired?.some(
    item => item.name === "oauth_provider"
  );
  useEffect(() => {
    if (stepProviders?.length === 1) {
      localStorage.setItem("rso_smart_login_state_id", stateId);
      localStorage.setItem("rso_selected_provider", stepProviders[0].name);
      localStorage.setItem("login_path", window.location.pathname);
      localStorage.setItem("rso_smart_login", "true");
      goToLink(getAuthUrl(application, stepProviders[0], "signup"));
    }
  }, [stepProviders]);

  const updateFormDate = (y, m, d, currentFieldName) => {
    const yearNum = Number(y);
    const monthNum = Number(m);
    let dayNum = Number(d);

    const checkDate = new DateObject({calendar: persian, year: yearNum, month: monthNum, day: 1});
    const maxDaysInMonth = checkDate.month.length || checkDate.daysInMonth;

    if (dayNum > maxDaysInMonth) {
      message.warning(i18next.t("general:invalid date"));
      dayNum = maxDaysInMonth;
    }

    setDateStates(prev => ({
      ...prev,
      [currentFieldName]: {year: yearNum, month: monthNum, day: dayNum},
    }));

    const date = new DateObject({calendar: persian, year: yearNum, month: monthNum, day: dayNum});
    const finalDateString = date.format("YYYY/MM/DD");
    form.setFieldsValue({[currentFieldName]: finalDateString});
  };

  useEffect(() => {
    const newDateStates = {};
    const defaultFormValues = {};

    Object.entries(fields).forEach(([key, f]) => {
      if (f.type === "date" || f.type === "birthday" || key === "birthday") {
        let y = 1402, m = 6, d = 15;

        const initialDateValue = formConfig.initialValues?.[key];
        if (initialDateValue) {
          const parts = initialDateValue.split(/[/-]/);
          if (parts.length >= 3) {
            y = Number(parts[0]);
            m = Number(parts[1]);
            d = Number(parts[2]);

            if (y > 1500) {
              const gregorianDate = new Date(y, m - 1, d);
              const formatter = new Intl.DateTimeFormat("en-US-u-ca-persian", {
                year: "numeric", month: "numeric", day: "numeric",
              });
              const shamsiParts = formatter.formatToParts(gregorianDate);
              y = Number(shamsiParts.find(p => p.type === "year").value);
              m = Number(shamsiParts.find(p => p.type === "month").value);
              d = Number(shamsiParts.find(p => p.type === "day").value);
            }
          }
        } else {
          const formattedMonth = String(m).padStart(2, "0");
          const formattedDay = String(d).padStart(2, "0");
          defaultFormValues[key] = `${y}/${formattedMonth}/${formattedDay}`;
        }
        newDateStates[key] = {year: y, month: m, day: d};
      }
    });

    setDateStates(prev => ({...prev, ...newDateStates}));

    if (Object.keys(defaultFormValues).length > 0) {
      form.setFieldsValue(defaultFormValues);
    }
  }, [formConfig.initialValues]);

  const handleActionClick = (item) => {
    if (item.type === "set_flag") {
      handleSubmit(item.flags);
    } else if (item.type === "internal_jump") {
      handleSubmit({step_action: {name: item.name}});
    }
  };

  return (
    <>
      <Form
        form={form}
        ref={formRef}
        component="form"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          ...formConfig.initialValues,
          phone:
            typeof formConfig.initialValues?.phone === "string" &&
            formConfig.initialValues.phone.length === 10 &&
            !formConfig.initialValues.phone.startsWith("0")
              ? `0${formConfig.initialValues.phone}`
              : formConfig.initialValues?.phone,
        }}
        style={{
          maxWidth: 400, margin: "auto", display: "flex",
          flexDirection: "column",
        }}
      >
        {/* title */}
        {(formConfig.stepName || formConfig.type) && (
          <>
            <div
              id={`${formConfig.stepName || formConfig.type}`}
              style={{
                marginBottom: 20,
                fontSize: 18,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {i18next.t(`general:${formConfig.type || formConfig.stepName}`)}
            </div>
            <div id={`${formConfig.stepName || formConfig.type}-helper-text`} />
          </>
        )}

        {/* error */}
        {/* {formConfig.errorMessage && (*/}
        {/*  <div*/}
        {/*    style={{*/}
        {/*      color: "red",*/}
        {/*      marginBottom: 16,*/}
        {/*      background: "#fff0f0",*/}
        {/*      padding: 10,*/}
        {/*      borderRadius: 4,*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {formConfig.errorMessage}*/}
        {/*  </div>*/}
        {/* )}*/}

        {/* fields */}
        {Object.entries(fields).map(([fieldName, field]) => {
          const {type, metadata = {}} = field;
          const label = i18next.t(
            `general:${metadata.label || fieldName}`
          );
          const currentState = dateStates[fieldName] || {year: 1402, month: 6, day: 15};
          if (
            fieldName === "is_employee" &&
            !application?.organizationObj?.enableEmployeeRegister
          ) {
            return null;
          }

          if (
            fieldName === "is_returning" ||
            (formConfig.type === "employee_check" &&
              fieldName === "is_employee")
          ) {
            return null;
          }
          if ((formConfig?.type || formConfig?.stepName) === "hona_aliveness_detection") {
            return null;
          }
          if (isForeigner && (formConfig.type === "employee_check" &&
            fieldName === "is_employee")) {
            return null;
          }
          if (type === "hidden") {
            return null;
          }
          const notRenderingLabel = fieldName !== "otp_code" && fieldName !== "oauth_provider";

          return (
            <Form.Item
              key={fieldName}
              name={fieldName}
              label={notRenderingLabel ? label : undefined}
              rules={
                !formConfig.type?.includes("verify")
                  ? buildRules({
                    fieldName,
                    fieldType: type,
                    metadata,
                    formRef,
                    passwordValidationApi: passwordApi,
                  })
                  : []
              }
              help={
                type === "password" && fieldName === "password"
                  ? passwordApi.passwordValidation.map(
                    (v) =>
                      v.show && (
                        <span
                          id={`validation-${v.passed}`}
                          key={v.rule}
                          style={{
                            textAlign: "start",
                            color: v.passed ? "green" : "red",
                            display: "block",
                          }}
                        >
                          {v.rule}
                        </span>
                      )
                  )
                  : fieldName === "confirm_password"
                    ? passwordApi.confirmValidation.map(
                      (v) =>
                        v.show && (
                          <span
                            id={`validation-${v.passed}`}
                            key={v.rule}
                            style={{
                              textAlign: "start",
                              color: v.passed ? "green" : "red",
                              display: "block",
                            }}
                          >
                            {v.rule}
                          </span>
                        )
                    )
                    : null
              }
            >
              {fieldName === "otp_code" ? (
                <RsoSendCodeOtp
                  value={otp.otpCode}
                  onChange={otp.setOtpCode}
                  method={sendCodeMethod}
                  application={application}
                  countryCode={country_code}
                  onButtonClickArgs={[
                    phone,
                    "phone",
                    "admin/" + application?.name,
                    name,
                  ]}
                  onComplete={(completedOtp) => {
                    form.validateFields()
                      .then((values) => {
                        handleSubmit({...values, otp_code: completedOtp});
                      })
                      .catch((errorInfo) => {
                        console.error("Form validation failed before auto-submit:", errorInfo);
                      });
                  }}
                />
              ) : fieldName === "country_code" ? (
                <CountryCodeSelect
                  style={{width: "100%"}}
                  countryCodes={
                    application.organizationObj?.countryCodes || []
                  }
                />
              ) : fieldName.startsWith("is_") ? (
                <Checkbox
                  checked={isEmployee}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setIsEmployee(newValue);
                    formRef.current?.setFieldsValue({[fieldName]: newValue});
                  }}
                >
                  {label}
                </Checkbox>
              ) : fieldName === "terms_of_use_accepted" ? (
                <>
                  <p
                    style={{
                      textAlign: "justify",
                      whiteSpace: "pre-line",
                      marginBottom: "10px",
                    }}
                  >
                    {i18next.t("general:Before completing the registration process, please read the following information carefully.\n" +
                      "Use of this platform requires your full acceptance of the “Terms of Use.”\n" +
                      "By accessing the link below, you can review the most up-to-date and authoritative version of these terms.\n" +
                      "Proceeding with the registration process constitutes your acknowledgment, understanding, and acceptance of all legal obligations outlined in the Terms of Use")}
                  </p>

                  <Checkbox
                    checked={formValues?.[fieldName]}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        formRef.current?.setFieldsValue({
                          terms_of_use_accepted: true,
                        });
                        form.setFieldsValue({
                          terms_of_use_accepted: true,
                        });
                        form.validateFields({validateOnly: true})
                          .then(() => setIsFormValid(true))
                          .catch(() => setIsFormValid(false));
                      } else {
                        formRef.current?.setFieldsValue({
                          terms_of_use_accepted: false,
                        });
                        form.setFieldsValue({
                          terms_of_use_accepted: false,
                        });
                        form.validateFields({validateOnly: true})
                          .then(() => setIsFormValid(true))
                          .catch(() => setIsFormValid(false));
                      }
                    }}
                  >
                    {i18next.t("signup:Accept")}{" "}
                    <a onClick={() => setTermsModalOpen(true)}>{i18next.t("signup:Terms of Use")}</a>
                  </Checkbox>

                  <AgreementModal
                    open={termsModalOpen}
                    application={application}
                    onOk={() => {
                      formRef.current?.setFieldsValue({
                        terms_of_use_accepted: true,
                      });
                      form.setFieldsValue({
                        terms_of_use_accepted: true,
                      });
                      setTermsModalOpen(false);
                    }}
                    onCancel={() => {
                      formRef.current?.setFieldsValue({
                        terms_of_use_accepted: false,
                      });
                      form.setFieldsValue({
                        terms_of_use_accepted: false,
                      });
                      setTermsModalOpen(false);
                    }}
                  />
                </>

              ) : type === "selector" ? (
                <Select placeholder={label}>
                  {metadata.options?.map((o) => (
                    <Select.Option key={o.value} value={o.value}>
                      {o.label}
                    </Select.Option>
                  ))}
                </Select>
              ) : fieldName === "passcode" ? (
                <Form.Item name="passcode"
                  rules={[{
                    required: true,
                    message: i18next.t("login:Please input your code!"),
                  }]}>
                  <RsoSendCodeInput
                    onSend={() => setMfaSent(true)}
                    isOtpSent={mfaSent}
                    countryCode={country_code}
                    method={"mfaAuth"}
                    onButtonClickArgs={[phone, mfaType === "sms" ? "phone" : mfaType, "admin/" + (application?.name || "admin/app-built-in"), name]}
                    codeValue={mfaCode} onChange={(value) => setMfaCode(value)}
                    application={application}
                  />
                </Form.Item>
              ) : type === "password" ? (
                <Input.Password className="with-addon-en-input" placeholder={label} />
              ) : type === "phone" ? (
                <Input
                  className="en-input"
                  maxLength={11}
                  disabled={detectedType === "phone"}
                />
              ) : type === "date" ? (
                <div style={{width: "100%", display: "flex", justifyContent: "center", direction: "ltr"}}>
                  <WheelPicker
                    year={currentState.year}
                    onChangeYear={(y) => updateFormDate(y, currentState.month, currentState.day, fieldName)}

                    month={currentState.month}
                    onChangeMonth={(m) => updateFormDate(currentState.year, m, currentState.day, fieldName)}

                    day={currentState.day}
                    onChangeDay={(d) => updateFormDate(currentState.year, currentState.month, d, fieldName)}
                  />
                </div>
              )
                : fieldName === "oauth_provider" ? (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    marginBottom: formConfig.dataRequired.length > 0 ? 0 : 20,
                  }}>
                    {formConfig.dataRequired?.map((provider) => (

                      <Button
                        key={provider.metadata.label}
                        type="default"
                        icon={<LoginOutlined />}
                        block
                        onClick={() => handleProviderClick(provider)}
                      >
                        {i18next.t("general:Continue with")} {provider.metadata.label}
                      </Button>
                    ))}
                  </div>

                ) : (
                  <Input
                    className="en-input"
                    disabled={
                      (detectedType === "nationalId" &&
                        fieldName === "nationalId") ||
                      (detectedType === "username" &&
                        fieldName === "username") ||
                      (detectedType === "phone" && fieldName === "phone")
                    }
                  />
                )}
            </Form.Item>
          );
        })}

        {/* captcha */}
        {captcha.renderCaptcha()}

        {/* forgot password */}
        {formConfig.type === "verify_password" &&
          <div style={{margin: "8px 0"}}>
            {Setting.renderForgetLink(application, i18next.t("login:Forgot password?"))}
          </div>
        }

        {(formConfig?.type || formConfig?.stepName) === "hona_aliveness_detection" &&
          <>
            <Form.Item name="personalPictureDataUri" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="testVideoDataUri" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="text" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="uniqueKey" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              key="nationalId"
              name="nationalId"
              hidden
            >
              <Input className="en-input" maxLength={10} />
            </Form.Item>

            <FaceCapturing
              promptText={honaPromptText}
              onComplete={(capturedData) => {
                form.setFieldsValue({
                  personalPictureDataUri: capturedData.photo_base64,
                  testVideoDataUri: capturedData.video_base64,
                });

              }}
            />
          </>
        }

        {formConfig.type === "employee_check" &&
          <div style={{margin: "8px 0"}} onClick={() => localStorage.setItem("ldap_state_id", stateId)}>
            {Setting.renderForgetLink(application, i18next.t("login:Forgot password?"))}
          </div>
        }
        {/* handle for set_flag */}
        {actions && actions.length > 0 && (
          <Flex gap={4} vertical align="start" style={{margin: "8px 0"}}>
            {actions.map((item, index) => (
              item.type === "external_redirect" ? (
                <a target="_blank" key={index} href={item.url} rel="noreferrer">{item.metadata.label}</a>
              ) : item.type === "internal_jump" ? (
                <Button type="link" key={index} style={{padding: "0", height: "fit-content"}}
                  onClick={() => handleActionClick(item)}>{item.metadata.label}</Button>
              ) : (
                <Button type="link" key={index} style={{padding: "0", height: "fit-content"}}
                  onClick={() => handleActionClick(item)}>{item.metadata.label}</Button>
              )
            ))}
          </Flex>
        )}

        {/* employee buttons */}
        {/* {formConfig.type === "employee_check" && (*/}
        {/*  <Button*/}
        {/*    block*/}
        {/*    type="dashed"*/}
        {/*    onClick={() => {*/}
        {/*      setIsEmployee(false);*/}
        {/*      employeeFlow.handleEmployeeAction(false, formConfig);*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {i18next.t("general:I'm not employee")}*/}
        {/*  </Button>*/}
        {/* )}*/}

        {/* {formConfig.type === "set_password" &&*/}
        {/*  application.organizationObj?.enableEmployeeRegister && (*/}
        {/*  <Button*/}
        {/*    block*/}
        {/*    type="dashed"*/}
        {/*    onClick={() => {*/}
        {/*      setIsEmployee(true);*/}
        {/*      employeeFlow.handleEmployeeAction(true, formConfig);*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {i18next.t("general:I'm employee")}*/}
        {/*  </Button>*/}
        {/* )}*/}

        {/* actions */}
        <div className="action-buttons" style={{display: "flex", flexDirection: "column", gap: 16, marginTop: 32}}>

          <div style={{display: "flex", gap: 16, width: "100%"}}>
            <Button
              size="large"
              id={`${formConfig.stepName || formConfig.type}-action-buttons-back`}
              className="action-buttons-back"
              style={{flex: 1}}
              onClick={() => goBackApi.canGoBack(formConfig) ? goBackApi.goBack(formConfig) : window.history.back()}
            >
              <RightOutlined rotate={i18next.language === "fa" ? 0 : 180} /> {i18next.t("general:Back")}
            </Button>

            <Button
              id={`${formConfig.stepName || formConfig.type}-action-buttons-forward`}
              size="large"
              className="action-buttons-forward"
              type="primary"
              htmlType="submit"
              style={{flex: 1}}
              disabled={
                !isFormValid ||
                !captcha.isCaptchaValid ||
                !otp.isOtpValid ||
                hasOauthProvider ||
                (fields["terms_of_use_accepted"] && !formValues?.terms_of_use_accepted) ||
                (fields["otp_code"] && otp.normalizedOtp.length !== 6) ||
                ((formConfig?.type || formConfig?.stepName) === "hona_aliveness_detection" &&
                  (!formValues?.testVideoDataUri || !formValues?.personalPictureDataUri))
              }
            >
              {i18next.t("general:Continue")}
            </Button>
          </div>

          {formConfig.isNextStepSkippable && (
            <Button
              size="large"
              type="solid"
              onClick={() => formConfig.onSubmit({skip_step: true})}
              style={{width: "100%", border: "unset"}}
            >
              {i18next.t("general:Skip this step")} <StepForwardOutlined rotate={i18next.language === "fa" ? 180 : 0} />
            </Button>
          )}

        </div>

      </Form>
    </>
  );
};

export default DynamicForm;
